import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@sg/hooks/useAutoSaveParams';

const deepClone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

interface IPipelineCalendarAutoSaveParams {
  isEditing: boolean;
  page: string;
  projects: any[];
  notesContent: string;
  applyProjects: (projects: any[]) => void;
  setNotesContent: (notesContent: string) => void;
}

interface IAutoSaveData {
  projects: any[];
  notesContent: string;
  baselineProjects: any[];
  baselineNotesContent: string;
}

const EMPTY_DATA: IAutoSaveData = {
  projects: [],
  notesContent: '',
  baselineProjects: [],
  baselineNotesContent: '',
};

export const usePipelineCalendarAutoSave = ({
  isEditing,
  page,
  projects,
  notesContent,
  applyProjects,
  setNotesContent,
}: IPipelineCalendarAutoSaveParams) => {
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const [baselineProjects, setBaselineProjects] = useState<any[]>([]);
  const [baselineNotesContent, setBaselineNotesContent] = useState<string>('');

  // 同一组件被 PortfolioOverview 与 Studios 等多个页面复用，
  // 使用 page 维度拼接独立的 pageKey，避免不同页面的编辑数据相互覆盖
  const autoSavePageKey = useMemo(
    () => `${EDIT_PAGE_KEY_MAP.studio_overview_pipeline_calendar}_${page || 'default'}`,
    [page],
  );
  const initialParamsRef = useRef<IAutoSaveData>(deepClone(EMPTY_DATA));

  const autoSaveMethods: any = useAutoSaveParams({
    pageKey: autoSavePageKey,
  });

  const {
    getSaveParamsValue,
    showRestorePrompt,
    deleteSaveParamsByPageKey,
  } = autoSaveMethods;

  const captureBaseline = useCallback(
    (projectList: any[], noteContent: string) => {
      const clonedProjects = deepClone(projectList || []);
      const clonedNote = noteContent || '';

      setBaselineProjects(clonedProjects);
      setBaselineNotesContent(clonedNote);

      initialParamsRef.current = {
        projects: clonedProjects,
        notesContent: clonedNote,
        baselineProjects: clonedProjects,
        baselineNotesContent: clonedNote,
      };
    },
    [],
  );

  const clearBaseline = useCallback(() => {
    setBaselineProjects([]);
    setBaselineNotesContent('');
    initialParamsRef.current = deepClone(EMPTY_DATA);
  }, []);

  const hasUnsavedChanges = useMemo(() => {
    if (!isEditing) {
      return false;
    }
    const projectsChanged =
      JSON.stringify(projects || []) !== JSON.stringify(baselineProjects || []);
    const notesChanged = (notesContent || '') !== (baselineNotesContent || '');
    return projectsChanged || notesChanged;
  }, [
    isEditing,
    projects,
    notesContent,
    baselineProjects,
    baselineNotesContent,
  ]);

  const currentSaveParams = useMemo(() => {
    if (!isEditing) {
      return deepClone(EMPTY_DATA);
    }
    return {
      projects: projects || [],
      notesContent: notesContent || '',
      baselineProjects: baselineProjects || [],
      baselineNotesContent: baselineNotesContent || '',
    };
  }, [
    isEditing,
    projects,
    notesContent,
    baselineProjects,
    baselineNotesContent,
  ]);

  useAutoSaveParams({
    pageKey: autoSavePageKey,
    initialParams: initialParamsRef.current,
    saveParamsCur: currentSaveParams,
    isBegin: isEditing,
    isChangeValue: true,
  });

  useEffect(() => {
    if (!isEditing) {
      initialParamsRef.current = deepClone(EMPTY_DATA);
    }
  }, [isEditing]);

  const handleEditWithAutoSave = useCallback(
    (onNoRestore: () => void) => {
      if (showRestorePrompt(autoSavePageKey)) {
        setAutoSaveVisible(true);
        return;
      }
      onNoRestore();
    },
    [showRestorePrompt, autoSavePageKey],
  );

  const handleAutoSaveConfirm = useCallback(() => {
    const savedData = (getSaveParamsValue(autoSavePageKey) || {}) as IAutoSaveData;
    setAutoSaveVisible(false);

    if (Array.isArray(savedData.projects) && savedData.projects.length > 0) {
      applyProjects(savedData.projects);
    }
    if (typeof savedData.notesContent === 'string') {
      setNotesContent(savedData.notesContent);
    }

    const restoredBaselineProjects =
      savedData.baselineProjects || savedData.projects || [];
    const restoredBaselineNotes =
      savedData.baselineNotesContent ?? savedData.notesContent ?? '';

    setBaselineProjects(restoredBaselineProjects);
    setBaselineNotesContent(restoredBaselineNotes);

    initialParamsRef.current = {
      projects: savedData.projects || [],
      notesContent: savedData.notesContent || '',
      baselineProjects: restoredBaselineProjects,
      baselineNotesContent: restoredBaselineNotes,
    };

    deleteSaveParamsByPageKey(autoSavePageKey);
  }, [
    getSaveParamsValue,
    autoSavePageKey,
    applyProjects,
    setNotesContent,
    deleteSaveParamsByPageKey,
  ]);

  const handleAutoSaveCancel = useCallback(() => {
    setAutoSaveVisible(false);
    deleteSaveParamsByPageKey(autoSavePageKey);
  }, [deleteSaveParamsByPageKey, autoSavePageKey]);

  const clearAutoSave = useCallback(() => {
    clearBaseline();
    deleteSaveParamsByPageKey(autoSavePageKey);
  }, [clearBaseline, deleteSaveParamsByPageKey, autoSavePageKey]);

  return {
    autoSaveVisible,
    hasUnsavedChanges,
    captureBaseline,
    clearBaseline,
    handleEditWithAutoSave,
    handleAutoSaveConfirm,
    handleAutoSaveCancel,
    clearAutoSave,
  };
};
